/*
 * Beauty Brands — GTM / GA4 data layer runtime
 *
 * Pushes the custom events listed in the Beauty Brands Migration Plan &
 * Data Layer Guide (navigation_click, social_media_click, cta_click,
 * plp_filter, plus form-success fires via snippets/data-layer-form-success).
 * Stape's e-commerce events are NOT handled here — Stape owns those.
 *
 * Global params (authentication_status, shopify_customer_id,
 * purchaser_type) are read from `window.__bbUser`, populated server-side
 * by `snippets/data-layer-init.liquid` before first paint. We never
 * re-resolve customer state in JS.
 *
 * See: DATA_LAYER_GUIDE.md
 */

const w = /** @type {any} */ (window);
const debugState = {
  enabled: false,
  wrapped: false,
};
const DEBUG_HISTORY_LIMIT = 200;

setupDebugTools();

if (!w.__bbDataLayerBound) {
  w.__bbDataLayerBound = true;

  // Theme-editor guard — avoid polluting the merchant's GA4 property.
  if (!document.documentElement.classList.contains('shopify-design-mode')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
    } else {
      bind();
    }
  }
}

/**
 * Push a custom event onto window.dataLayer with the global user params
 * re-attached. No-op when `dataLayer` is unavailable.
 *
 * Exposed on window as `__bbPush` so inline form-success scripts can
 * fire events without importing this module.
 *
 * @param {string} event
 * @param {Record<string, unknown>} [payload]
 */
function push(event, payload) {
  if (!Array.isArray(w.dataLayer)) return;
  const user = w.__bbUser || {};
  w.dataLayer.push({
    event,
    ...(payload || {}),
    authentication_status: user.user_authentication_status ?? null,
    shopify_customer_id: user.user_shopify_customer_id ?? null,
    purchaser_type: user.user_purchaser_type ?? null,
  });
}

w.__bbPush = push;

function bind() {
  document.addEventListener('click', onDocumentClick, false);
  document.addEventListener('change', onDocumentChange, false);
}

/** @param {MouseEvent} event */
function onDocumentClick(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  // Priority: social → plp menu → navigation → cta.
  if (handleSocialClick(target)) return;
  if (handlePlpMenuClick(target)) return;
  if (handleNavigationClick(target)) return;
  if (handleCtaClick(target)) return;
}

/**
 * @param {Element} target
 * @returns {boolean}
 */
function handleSocialClick(target) {
  const link = /** @type {HTMLAnchorElement | null} */ (
    target.closest('a[data-social-link], a.footer__social-link')
  );
  if (!link) return false;

  const platform =
    (link.dataset.socialLink && String(link.dataset.socialLink).trim()) ||
    platformFromAriaLabel(link.getAttribute('aria-label')) ||
    textFor(link);

  push('social_media_click', {
    social_media_platform: platform,
    click_url: link.href,
  });
  return true;
}

/**
 * PLP vertical subcollection menu (collection-subcollections section).
 * @param {Element} target
 * @returns {boolean}
 */
function handlePlpMenuClick(target) {
  const link = /** @type {HTMLAnchorElement | null} */ (
    target.closest('a[data-plp-menu], a.collection-subcollections__link')
  );
  if (!link) return false;
  if (link.closest('[data-cta-track]')) return false;

  push('plp_menu', {
    click_text: textFor(link),
    click_url: link.href,
  });
  return true;
}

/**
 * @param {Element} target
 * @returns {boolean}
 */
function handleNavigationClick(target) {
  const link = /** @type {HTMLAnchorElement | null} */ (target.closest('a'));
  if (!link) return false;
  if (link.closest('[data-social-link], .footer__social-link')) return false;
  if (link.closest('[data-cta-track]')) return false;
  if (link.closest('[data-plp-menu], .collection-subcollections')) return false;

  // Focal: store-header + mobile drawer (append-body) + section group.
  const inHeader = link.closest(
    'store-header, .header, #shopify-section-header, #header-group, #shopify-section-group-header-group, #mobile-menu-drawer, mobile-navigation'
  );
  const inFooter = link.closest('footer, .footer');
  // Collection/product/blog breadcrumbs live in main content, not header/footer.
  const inBreadcrumb = link.closest('.breadcrumb, nav.breadcrumb');
  if (!inHeader && !inFooter && !inBreadcrumb) return false;

  // Skip cart / checkout utility actions — not nav menu clicks.
  if (link.closest('[name="checkout"], .checkout-button, mini-cart')) return false;

  let menuType = 'footer';
  if (inHeader) menuType = 'header';
  else if (inBreadcrumb) menuType = 'breadcrumb';

  let menuCategory = '';
  let menuSubcategory = '';
  if (inBreadcrumb) {
    // Parent crumb in the trail (e.g. Home → Hair → … → category = previous item).
    const item = link.closest('.breadcrumb__item');
    const previousItem = item instanceof HTMLElement ? item.previousElementSibling : null;
    menuCategory = previousItem
      ? textFor(previousItem.querySelector('.breadcrumb__link, a, span')) || 'Breadcrumb'
      : 'Breadcrumb';
  } else {
    const parent = link.closest('[data-nav-parent]');
    menuCategory = parent instanceof HTMLElement ? parent.dataset.navParent || '' : '';
    // Mid-level group (e.g. "Top Brands" / "Shop by Category" under Makeup).
    const subcategory = link.closest('[data-nav-subcategory]');
    menuSubcategory =
      subcategory instanceof HTMLElement ? subcategory.dataset.navSubcategory || '' : '';
  }

  push('navigation_click', {
    click_text: textFor(link),
    click_url: link.href,
    menu_type: menuType,
    menu_category: menuCategory,
    menu_subcategory: menuSubcategory,
  });
  return true;
}

/**
 * @param {Element} target
 * @returns {boolean}
 */
function handleCtaClick(target) {
  let cta = /** @type {HTMLElement | null} */ (target.closest('[data-cta-track]'));
  const explicitTrack = !!cta;

  // Fallback for marketing CTAs that were not yet tagged with data-cta-track.
  if (!cta) {
    cta = /** @type {HTMLElement | null} */ (
      target.closest(
        [
          'a.button',
          'a.multi-column__link',
          'a.multi-column__image-wrapper',
          'a.multi-column__button',
          'a.promotion-block',
          'a.list-collections__item',
          'a.slideshow__button',
          'a.slideshow__slide-inner',
          'a.logo-list__item',
          'a.sale-promo-banner__link',
          'a.image-with-text-overlay__button',
          'a.announcement-bar__action-link',
        ].join(', ')
      )
    );
    if (!cta) return false;
  }

  // Explicit data-cta-track: allow main body + header chrome (announcement bar, etc).
  // Untagged fallbacks: main body only.
  const inMain = !!cta.closest('main, #main, #MainContent, [role="main"]');
  const inHeaderChrome = !!cta.closest(
    '.announcement-bar, .shopify-section--announcement-bar, announcement-bar, store-header, .header, #shopify-section-header, #shopify-section-group-header-group, #mobile-menu-drawer, mobile-navigation'
  );
  if (explicitTrack) {
    if (!inMain && !inHeaderChrome) return false;
  } else if (!inMain) {
    return false;
  }

  // Exclude cart / product-form / checkout actions from CTA tracking.
  if (
    cta.closest(
      'product-form, .product-form, form[action*="/cart"], mini-cart, #mini-cart, .cart, [data-product-add-to-cart-button]'
    )
  ) {
    return false;
  }
  if (cta.matches('[name="checkout"], .checkout-button') || cta.closest('[name="checkout"], .checkout-button')) {
    return false;
  }

  const url =
    cta instanceof HTMLAnchorElement
      ? cta.href
      : cta.dataset.ctaUrl || '';

  // Never fall back to the section heading here. For an image-only CTA that resolved
  // to the nearest preceding heading, which is the *previous* card's label.
  const clickText =
    (cta.dataset.ctaLabel && String(cta.dataset.ctaLabel).trim()) ||
    textFor(cta) ||
    resolveLocalLabel(cta) ||
    'CTA';

  push('cta_click', {
    click_text: clickText,
    click_url: url,
    section_heading:
      (cta.dataset.ctaSection && String(cta.dataset.ctaSection).trim()) ||
      resolveSectionHeading(cta),
  });
  return true;
}

/**
 * PLP / search facet apply:
 * - list/boolean filters: checkbox checked
 * - price range: number input change (also fired when range sliders sync)
 * Sort-by is excluded (not a product filter facet).
 * @param {Event} event
 */
function onDocumentChange(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.closest('facet-filters, #facet-filters-form, .product-facet__filters')) return;

  const priceRange = target.closest('price-range');
  if (priceRange) {
    // Sliders sync into the number fields and bubble `change` from those inputs.
    if (target.type === 'number' || target.classList.contains('input-prefix__field')) {
      pushPriceRangeFilter(priceRange);
    }
    return;
  }

  if (target.type !== 'checkbox' || !target.checked) return;

  const group = target.closest('[data-filter-category], .product-facet__filter-item');
  let filterCategory = '';
  if (group instanceof HTMLElement) {
    filterCategory =
      group.dataset.filterCategory ||
      textFor(group.querySelector('.collapsible-toggle, .collapsible-toggle label, label')) ||
      '';
  }

  const filterValue =
    target.dataset.label ||
    target.getAttribute('title') ||
    target.getAttribute('aria-label') ||
    labelTextForInput(target) ||
    target.value ||
    '';

  push('plp_filter', {
    filter_category: filterCategory,
    filter_value: filterValue,
  });
}

/**
 * @param {Element} priceRange
 */
function pushPriceRangeFilter(priceRange) {
  const group = priceRange.closest('[data-filter-category], .product-facet__filter-item');
  const filterCategory =
    group instanceof HTMLElement
      ? group.dataset.filterCategory ||
        textFor(group.querySelector('.collapsible-toggle, .collapsible-toggle label, label')) ||
        'Price'
      : 'Price';

  const numberInputs = priceRange.querySelectorAll(
    '.price-range__input input[type="number"], .input-prefix__field'
  );
  const minInput = numberInputs[0];
  const maxInput = numberInputs[1];
  if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;

  // Require at least one bound to be set (matches "user applied a filter").
  if (minInput.value === '' && maxInput.value === '') return;

  const currencyEl = priceRange.querySelector('.input-prefix__value');
  const currency =
    currencyEl instanceof HTMLElement ? textFor(currencyEl) : '';

  const min = minInput.value !== '' ? minInput.value : minInput.min || minInput.placeholder || '0';
  const max = maxInput.value !== '' ? maxInput.value : maxInput.max || maxInput.placeholder || '';
  const filterValue = currency
    ? `${currency}${min} - ${currency}${max}`
    : `${min} - ${max}`;

  push('plp_filter', {
    filter_category: filterCategory,
    filter_value: filterValue,
  });
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function labelTextForInput(input) {
  if (input.id) {
    const safeId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(input.id)
        : input.id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const label = document.querySelector(`label[for="${safeId}"]`);
    if (label) {
      return textFor(label).replace(/\s*\(\d+\)\s*$/, '').trim();
    }
  }
  const wrappingLabel = input.closest('label');
  return wrappingLabel ? textFor(wrappingLabel) : '';
}

/**
 * @param {string | null} aria
 * @returns {string}
 */
function platformFromAriaLabel(aria) {
  if (!aria) return '';
  // e.g. "Follow on Facebook" / "Follow us on Instagram"
  const match = aria.match(/\b(Facebook|Instagram|Twitter|X|Pinterest|TikTok|YouTube|LinkedIn)\b/i);
  return match ? match[1] : aria.trim();
}

/**
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function textFor(el) {
  if (!el) return '';
  const txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (txt) return txt;
  if (el instanceof HTMLElement) {
    return el.getAttribute('aria-label')?.trim() || el.title?.trim() || '';
  }
  return '';
}

/**
 * Repeated card/item containers. A heading inside one of these describes that card,
 * not the section, and not any neighbouring card.
 */
const CTA_ITEM_SELECTOR = [
  '.multi-column__item',
  '.promotion-block',
  '.list-collections__item',
  '.logo-list__item',
  '.timeline__item',
  '.image-with-text__item',
  '.product-item',
  '.slideshow__slide',
].join(', ');

/**
 * Label for a CTA that has no text of its own (e.g. an image-wrapper link):
 * the heading of its own card, else the image alt. Never looks outside the card.
 *
 * @param {HTMLElement} cta
 * @returns {string}
 */
function resolveLocalLabel(cta) {
  const item = cta.closest(CTA_ITEM_SELECTOR);
  if (item) {
    const heading = textFor(item.querySelector('h1, h2, h3, h4, h5, h6, .heading'));
    if (heading) return heading;
  }

  const img = cta.querySelector('img[alt]');
  return img instanceof HTMLImageElement ? img.alt.trim() : '';
}

/**
 * @param {HTMLElement} cta
 * @returns {string}
 */
function resolveSectionHeading(cta) {
  const explicitSection = cta.closest('[data-cta-section]');
  if (explicitSection instanceof HTMLElement) {
    const explicit = explicitSection.dataset.ctaSection || '';
    if (explicit) return explicit;
  }

  // Slideshow slides often have boxed + primary + secondary headings — join them.
  const slideRoot = cta.closest('.slideshow__slide, .slideshow__slide-inner');
  if (slideRoot instanceof HTMLElement) {
    const joined = joinSlideHeadings(slideRoot);
    if (joined) return joined;
  }

  // The section's own <header> heading — "Latest Deals", "Shop by Category", etc.
  // Theme convention is `<h3 class="heading h2">` for the title and
  // `<h2 class="heading heading--small">` for the subheading above it.
  const sectionRoot = cta.closest('.shopify-section, section');
  if (sectionRoot) {
    const sectionHeader = sectionRoot.querySelector('.section__header');
    if (sectionHeader && !sectionHeader.contains(cta)) {
      const heading =
        sectionHeader.querySelector('.heading.h2, h3.heading') ||
        sectionHeader.querySelector('h1, h2, h3, h4, h5, h6, .heading');
      const text = textFor(heading);
      if (text) return text;
    }
  }

  const localRoot = cta.closest(
    '.promotion-block, .image-with-text, .image-with-text-overlay, .rich-text, .multi-column__item, .list-collections__item, .logo-list__item, .sale-promo-banner, .timeline__item, .featured-collections, .product-list'
  );
  if (localRoot instanceof HTMLElement) {
    const local = findClosestPrecedingHeading(cta, localRoot);
    if (local) return local;
  }

  return findClosestPrecedingHeading(
    cta,
    cta.closest('.shopify-section, main, #main, #MainContent, [role="main"]')
  );
}

/**
 * Join all heading-like labels in a slideshow slide (order preserved).
 * @param {Element} root
 * @returns {string}
 */
function joinSlideHeadings(root) {
  const parts = [];
  const seen = new Set();
  root
    .querySelectorAll(
      '.slideshow__boxed-heading[data-cta-heading], .slideshow__heading[data-cta-heading], .slideshow__boxed-heading, .slideshow__heading'
    )
    .forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const text =
        (el.dataset.ctaHeading && String(el.dataset.ctaHeading).trim()) || textFor(el);
      if (!text || seen.has(text)) return;
      seen.add(text);
      parts.push(text);
    });
  return parts.join(' | ');
}

/**
 * @param {HTMLElement} from
 * @param {Element | null} root
 * @returns {string}
 */
function findClosestPrecedingHeading(from, root) {
  if (!root) return '';

  const ownItem = from.closest(CTA_ITEM_SELECTOR);
  const candidates = root.querySelectorAll(
    'h1, h2, h3, h4, h5, h6, [data-cta-heading]'
  );
  let best = '';

  for (const el of candidates) {
    if (!(el instanceof HTMLElement)) continue;
    if (el === from || from.contains(el)) continue;
    if (!(from.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING)) continue;

    // A heading inside a different card belongs to that card. Without this, a CTA in
    // card 4 picks up card 3's heading, since that is the nearest preceding heading.
    const headingItem = el.closest(CTA_ITEM_SELECTOR);
    if (headingItem && headingItem !== ownItem) continue;

    const headingText = el.dataset.ctaHeading?.trim() || textFor(el);
    if (headingText) best = headingText;
  }

  return best;
}

function setupDebugTools() {
  if (!Array.isArray(w.__bbDataLayerEvents)) {
    w.__bbDataLayerEvents = [];
  }

  debugState.enabled = isDebugEnabled();
  wrapDataLayerPush();

  w.__bbDataLayerDebug = {
    enable() {
      debugState.enabled = true;
      try {
        w.localStorage.setItem('bbDataLayerDebug', '1');
      } catch (_) {}
      console.info('[BB data-layer] debug enabled');
    },
    disable() {
      debugState.enabled = false;
      try {
        w.localStorage.removeItem('bbDataLayerDebug');
      } catch (_) {}
      console.info('[BB data-layer] debug disabled');
    },
    state() {
      return {
        enabled: debugState.enabled,
        eventsBuffered: w.__bbDataLayerEvents.length,
      };
    },
    dump() {
      return w.__bbDataLayerEvents.slice();
    },
    clear() {
      w.__bbDataLayerEvents = [];
      console.info('[BB data-layer] debug buffer cleared');
    },
  };

  if (debugState.enabled) {
    console.info('[BB data-layer] debug logging is enabled');
  }
}

function wrapDataLayerPush() {
  if (debugState.wrapped) return;
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }
  if (typeof w.dataLayer.push !== 'function') return;

  const nativePush = w.dataLayer.push.bind(w.dataLayer);
  w.dataLayer.push = function wrappedPush(...entries) {
    for (const entry of entries) {
      recordDataLayerEntry(entry);
    }
    return nativePush(...entries);
  };

  debugState.wrapped = true;
}

/**
 * @param {unknown} entry
 */
function recordDataLayerEntry(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;

  const eventEntry = /** @type {Record<string, unknown>} */ (entry);
  const snapshot = {
    timestamp: new Date().toISOString(),
    ...eventEntry,
  };

  w.__bbDataLayerEvents.push(snapshot);
  if (w.__bbDataLayerEvents.length > DEBUG_HISTORY_LIMIT) {
    w.__bbDataLayerEvents.shift();
  }

  if (debugState.enabled) {
    const label = typeof eventEntry.event === 'string' ? eventEntry.event : '(no event name)';
    console.log('[BB data-layer]', label, snapshot);
  }
}

function isDebugEnabled() {
  if (w.__BB_DATA_LAYER_DEBUG === true) return true;
  try {
    if (w.localStorage.getItem('bbDataLayerDebug') === '1') return true;
  } catch (_) {}
  const params = new URLSearchParams(w.location.search);
  return params.get('dl_debug') === '1';
}
